import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MemoryRouter, Routes, Route } from 'react-router-dom';
import { apiList } from '../api/apiClient';
import { VisitorDirectory } from './visitor/VisitorDirectory';
import { DebugLog, useDebugLog, DebugLogHandle } from '../components/visitor/DebugLog';

const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';

interface PropertyRow {
  id: number;
  name: string;
  property_uuid?: string;
  address?: string;
  city?: string;
  state?: string;
  visitor_enabled?: boolean;
}

const propKey = (p: PropertyRow) => p.property_uuid || String(p.id);

interface FeatureFlags {
  enableLiveStatus: boolean;
  enableSearchHighlights: boolean;
  enableTenantPhotos: boolean;
  enableQuickDial: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  enableLiveStatus: false,
  enableSearchHighlights: false,
  enableTenantPhotos: false,
  enableQuickDial: false,
};

/** Wrap fetch so all visitor-side network traffic shows up in the debug log. */
const useFetchInterceptor = (log: DebugLogHandle, active: boolean) => {
  useEffect(() => {
    if (!active) return;
    const orig = window.fetch.bind(window);
    (window as any).__origFetch = (window as any).__origFetch || orig;
    window.fetch = async (input: any, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : input?.url || '[unknown]';
      const method = (init?.method || 'GET').toUpperCase();
      const t = Date.now();
      try {
        const res = await orig(input, init);
        const dt = Date.now() - t;
        const lvl = res.ok ? 'success' : res.status < 500 ? 'warn' : 'error';
        log[lvl](`${method} ${url} → ${res.status} (${dt}ms)`);
        return res;
      } catch (e: any) {
        log.error(`${method} ${url} threw`, e);
        throw e;
      }
    };
    return () => {
      window.fetch = (window as any).__origFetch || orig;
    };
  }, [active, log]);
};

export const BetaVisitorDirectory: React.FC = () => {
  const log = useDebugLog();
  const [properties, setProperties] = useState<PropertyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<PropertyRow | null>(null);
  const [simKey, setSimKey] = useState(0);
  const [intercept, setIntercept] = useState(true);
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useFetchInterceptor(log, intercept);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    (async () => {
      log.info('Beta simulator booted');
      log.info('Loading properties …');
      try {
        const data = await apiList('properties');
        const list: PropertyRow[] = Array.isArray(data) ? data : (data as any).items || [];
        setProperties(list);
        log.success(`Loaded ${list.length} properties`);
      } catch (err: any) {
        log.error('Failed to list properties', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter((p) =>
      [p.name, p.address, p.city, p.state, p.property_uuid, String(p.id)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [properties, search]);

  const selectProperty = (p: PropertyRow) => {
    setSelected(p);
    setOpen(false);
    setSearch('');
    setSimKey((k) => k + 1);
    log.info(`── Mounting simulator for "${p.name}" (${propKey(p)}) ──`);
    runHealthChecks(p);
  };

  const runHealthChecks = async (p: PropertyRow) => {
    const checks: Array<{ label: string; url: string; method?: string; body?: any }> = [
      { label: 'Visitor units', url: `${API}/visitor/${p.id}/units` },
      { label: 'Visitor settings', url: `${API}/visitor/${p.id}/settings` },
      {
        label: 'Intercom unlock-pin probe',
        url: `${API}/intercom/unlock-pin`,
        method: 'POST',
        body: { property_id: p.id, pin: '0000' },
      },
      {
        label: 'Face verify probe',
        url: `${API}/face/verify-kiosk`,
        method: 'POST',
        body: { property_id: p.id, image_base64: '' },
      },
    ];
    for (const c of checks) {
      try {
        const t = Date.now();
        const r = await fetch(c.url, {
          method: c.method || 'GET',
          headers: c.body ? { 'Content-Type': 'application/json' } : undefined,
          body: c.body ? JSON.stringify(c.body) : undefined,
        });
        log[r.ok ? 'success' : r.status < 500 ? 'warn' : 'error'](
          `${c.label}: ${r.status} (${Date.now() - t}ms)`,
        );
      } catch (e: any) {
        log.error(`${c.label}: unreachable`, e);
      }
    }
  };

  const reset = () => {
    setSimKey((k) => k + 1);
    if (selected) {
      log.info('Resetting simulator …');
      runHealthChecks(selected);
    }
  };

  const toggleFlag = (k: keyof FeatureFlags) => {
    setFlags((f) => {
      const next = { ...f, [k]: !f[k] };
      log.info(`Flag ${k} = ${next[k] ? 'ON' : 'OFF'}`);
      return next;
    });
  };

  return (
    <div className="shadow-page-frame">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Beta Visitor Directory
            <span className="ml-2 align-middle text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              SIMULATOR
            </span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            In-app sandbox of the visitor directory for trying new features against any property's live data.
          </p>
        </div>
        {selected && (
          <button
            onClick={reset}
            className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm text-gray-700"
          >
            Reset simulator
          </button>
        )}
      </div>

      {/* Selector */}
      <div ref={dropdownRef} className="relative mb-4 max-w-lg">
        <button
          onClick={() => setOpen((v) => !v)}
          disabled={loading}
          className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-left text-sm hover:border-gray-400 disabled:opacity-50"
        >
          <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
            {loading
              ? 'Loading properties…'
              : selected
                ? `${selected.name}${selected.address ? ` — ${selected.address}` : ''}`
                : 'Select a property…'}
          </span>
          <svg className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <div className="absolute left-0 right-0 mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            <div className="p-2 border-b border-gray-100">
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, city, UUID, or ID…"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div className="max-h-72 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-gray-400">No matches</div>
              ) : (
                filtered.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => selectProperty(p)}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-0"
                  >
                    <div className="text-sm font-medium text-gray-900">{p.name}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {[p.address, p.city, p.state].filter(Boolean).join(', ') || '—'}
                      {' · '}
                      <code>{propKey(p)}</code>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Feature flags */}
      <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
            Experimental Features
          </span>
          <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
            <input
              type="checkbox"
              checked={intercept}
              onChange={(e) => setIntercept(e.target.checked)}
              className="accent-brand-500"
            />
            Intercept fetch (log all network)
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(DEFAULT_FLAGS) as Array<keyof FeatureFlags>).map((k) => (
            <button
              key={k}
              onClick={() => toggleFlag(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                flags[k]
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
              }`}
            >
              {flags[k] ? '● ' : '○ '}
              {k}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-gray-400">
          Toggles are exposed to the simulator via <code>window.__betaFlags</code> — wire new
          experiments here as they are added to <code>VisitorDirectory</code>.
        </p>
      </div>

      {/* Sim + log */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-4">
        <div className="bg-gray-100 rounded-xl border border-gray-200 overflow-hidden" style={{ minHeight: 700 }}>
          {selected ? (
            <SimulatorFrame propertyKey={propKey(selected)} flags={flags} simKey={simKey} />
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-gray-400 py-32">
              Select a property to launch the simulator.
            </div>
          )}
        </div>
        <DebugLog log={log} title="Service & Network Debug Log" />
      </div>
    </div>
  );
};

interface SimulatorFrameProps {
  propertyKey: string;
  flags: FeatureFlags;
  simKey: number;
}

const SimulatorFrame: React.FC<SimulatorFrameProps> = ({ propertyKey, flags, simKey }) => {
  // Expose flags globally so experimental code in VisitorDirectory can read them.
  useEffect(() => {
    (window as any).__betaFlags = flags;
  }, [flags]);

  return (
    <div className="w-full" style={{ height: 760, overflow: 'auto', backgroundColor: 'white' }}>
      <MemoryRouter key={simKey} initialEntries={[`/${propertyKey}`]}>
        <Routes>
          <Route path="/:propertyId" element={<VisitorDirectory />} />
          <Route path="*" element={<VisitorDirectory />} />
        </Routes>
      </MemoryRouter>
    </div>
  );
};

export default BetaVisitorDirectory;
